import { ReactNode, useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form'

import { useTableContext } from '@/components/new'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'

interface FilterItem {
  title: string
  titleIcon?: ReactNode
  filterCode: string
  component: (_useFormFilter: UseFormReturn<FieldValues>) => ReactNode
  filterToDisplayNameMapper: (_value: unknown) => string
}
export interface AdvancedFiltersList {
  filters: FilterItem[]
  onSubmit: (_filters: Record<string, unknown>) => void
}

interface Props {
  selectedFilters: Record<string, string>
  setSelectedFilters: (_selected: Record<string, string>) => void
}
export const AdvancedFilters = ({
  selectedFilters,
  setSelectedFilters,
}: Props) => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const { advancedFilterList } = useTableContext()
  const filterForm = useForm()
  const { handleSubmit, reset } = filterForm
  const onApplyFilters = (filters: Record<string, unknown>) => {
    if (!advancedFilterList) return
    const appliedFilters: Record<string, string> = {}
    Object.keys(filters).forEach((key) => {
      const value = filters[key]
      const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value)
      if (hasValue) {
        const filtr = advancedFilterList.filters.find(
          (item) => item.filterCode === key
        )
        if (filtr) {
          appliedFilters[filtr.title] = filtr.filterToDisplayNameMapper(value)
        }
      }
    })
    setSelectedFilters(appliedFilters)
    setIsFilterDrawerOpen(false)
  }

  return (
    <Sheet onOpenChange={setIsFilterDrawerOpen} open={isFilterDrawerOpen}>
      <SheetTrigger asChild>
        <Button
          aria-label="Filtros avanzados"
          className="gap-1.5"
          variant="outline"
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Filtros avanzados</span>
          {Object.keys(selectedFilters).length > 0 && (
            <Badge className="ml-1 rounded-full px-1.5" variant="secondary">
              {Object.keys(selectedFilters).length}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Filtros avanzados</SheetTitle>
          <SheetDescription>
            Configura los filtros para encontrar exactamente lo que buscas.
          </SheetDescription>
        </SheetHeader>
        <Form {...filterForm}>
          <ScrollArea className="h-[calc(100vh-180px)] pr-4">
            {advancedFilterList?.filters.map((filter) => {
              return (
                <div
                  className="flex flex-col space-y-6 py-6"
                  key={filter.filterCode}
                >
                  <div className="flex items-center space-x-2">
                    <h3 className="text-sm font-medium">
                      {filter.title || 'Misceláneos'}
                    </h3>
                    {filter?.titleIcon && filter.titleIcon}
                  </div>
                  {filter.component(filterForm)}
                  <Separator />
                </div>
              )
            })}
          </ScrollArea>

          <SheetFooter className="flex-row justify-between gap-2 sm:justify-between">
            <Button
              className="flex-1"
              onClick={() => {
                reset()
                setSelectedFilters({})
                if (advancedFilterList) {
                  advancedFilterList.onSubmit({})
                }
                setIsFilterDrawerOpen(false)
              }}
              variant="outline"
            >
              Limpiar
            </Button>
            <Button
              className="flex-1"
              onClick={handleSubmit((filters) => {
                if (advancedFilterList) {
                  advancedFilterList.onSubmit(filters)
                }
                onApplyFilters(filters)
              })}
            >
              Aplicar
            </Button>
          </SheetFooter>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
